import type {
  AbstractTemplateInOut,
  SubTemplateValueSetupInOut,
  TemplateCreateOperation,
} from '@festina/configuration-general-types/configuration'
import type {
  TemplateReferenceFieldNode,
  TemplateReferenceNode,
  TemplateReferenceVersionNode,
  TemplateTypeReferenceNode,
  TemplateTypeReferenceNodes,
} from '@festina/life-general-types/life'
import type { LayerKey } from '@festina-life/api'
import { assertNotEmpty, notEmpty, type Optional } from '@festina-life/core'

import { getLayerMetaModelFromMap } from '../../context/metaModelHelpers'
import type {
  MetaModel,
  ModelMetaType,
  PropertyDescriptor,
} from '../../types/metaModel'
import type {
  TemplateFieldReferenceFull,
  TemplateReferenceNodeFull,
  TemplateVersionReferenceNodeFull,
} from '../../types/templateModel'
import { stringToDateTimeNullSafe } from '../helpers'
import { getTemplateProperty } from '../templateOperations/helpers'
import type { OperationWithSourceInfo } from '../types'

export const addKeyValuePairToMap = ({
  classMap,
  className,
  key,
  value,
}: {
  classMap: Map<
    string,
    Map<string, OperationWithSourceInfo<TemplateCreateOperation>[]>
  >
  className: string
  key: string
  value: OperationWithSourceInfo<TemplateCreateOperation>[]
}) => {
  if (!classMap.has(className)) {
    classMap.set(className, new Map())
  }

  if (!classMap.get(className)!.get(key)) {
    classMap.get(className)!.set(key, [])
  }
  classMap
    .get(className)
    ?.get(key)
    ?.push(...value)
}

export const toReferenceModel = ({
  operations,
  name,
  layeredMetaModel,
}: {
  operations: Map<
    string,
    Map<string, OperationWithSourceInfo<TemplateCreateOperation>[]>
  >
  name: string
  layeredMetaModel: Map<Optional<string>, MetaModel>
}): TemplateTypeReferenceNodes => {
  const collector: TemplateTypeReferenceNode[] = []
  collectTemplateTypeReferenceNodes({
    currentType: name,
    operations,
    seen: new Set(),
    collector,
    layeredMetaModel,
  })

  return { _type: 'TemplateTypeReferenceNodes', nodes: collector }
}

const collectTemplateTypeReferenceNodes = ({
  currentType,
  operations,
  seen,
  collector,
  layeredMetaModel,
}: {
  currentType: string
  operations: Map<
    string,
    Map<string, OperationWithSourceInfo<TemplateCreateOperation>[]>
  >
  seen: Set<string>
  collector: TemplateTypeReferenceNodes[]
  layeredMetaModel: Map<Optional<string>, MetaModel>
}) => {
  if (seen.has(currentType)) {
    return []
  }
  seen.add(currentType)

  const sources = operations.get(currentType)

  if (!sources) {
    return []
  }

  const thisClassNode: TemplateTypeReferenceNode = {
    _type: 'TemplateTypeReferenceNode',
    typeName: currentType,
    rootReferences: [...sources.entries()].map(([key, value]) =>
      collectTemplateSourceReferenceNode({
        key,
        value,
        operations,
        layeredMetaModel,
        seen,
        collector,
      }),
    ),
  }

  collector.push(thisClassNode)

  return undefined
}

type VariantsAndProperty = {
  property: PropertyDescriptor
  variants: string[]
}

const getReferenceProperties = <T extends AbstractTemplateInOut>(
  currentTemplate: T,
  metaModel: MetaModel,
): VariantsAndProperty[] => {
  assertNotEmpty(currentTemplate.templateTypeName)

  const type = metaModel.types.get(
    currentTemplate.templateTypeName,
  ) as Optional<ModelMetaType>

  if (!type) {
    return []
  }

  const fields = type.properties
    .filter((p) => p.typeName === 'TemplateValueSetup')
    .map((p) => ({
      property: p,
      variants: navigateTemplateToReferences(currentTemplate, p),
    }))

  const nested = type.properties
    .filter((p) =>
      ['SingleSubTemplateValueSetup', 'MultipleSubTemplateValueSetup'].includes(
        p.typeName,
      ),
    )
    .flatMap((p) => {
      const newTemplate = getTemplateProperty(currentTemplate, p.name)
        ?.value as SubTemplateValueSetupInOut

      return (
        newTemplate?.variants?.flatMap((variant) =>
          getReferenceProperties(variant as AbstractTemplateInOut, metaModel),
        ) ?? []
      )
    })

  return fields.concat(nested)
}

const collectTemplateSourceReferenceNode = ({
  key,
  value,
  operations,
  layeredMetaModel,
  seen,
  collector,
}: {
  key: string
  value: OperationWithSourceInfo<TemplateCreateOperation>[]
  operations: Map<
    string,
    Map<string, OperationWithSourceInfo<TemplateCreateOperation>[]>
  >
  layeredMetaModel: Map<Optional<string>, MetaModel>
  seen: Set<string>
  collector: TemplateTypeReferenceNodes[]
}): TemplateReferenceNode =>
  ({
    _type: 'TemplateReferenceNode',
    _id: key,
    versions: value.map((v) =>
      collectTemplateVersionReferenceNode({
        operation: v,
        operations,
        layeredMetaModel,
        seen,
        collector,
      }),
    ),
  }) as {
    _type: TemplateReferenceNode['_type']
    _id: string
    versions: TemplateReferenceVersionNode[]
  }

const collectTemplateVersionReferenceNode = ({
  operation,
  operations,
  layeredMetaModel,
  seen,
  collector,
}: {
  operation: OperationWithSourceInfo<TemplateCreateOperation>
  operations: Map<
    string,
    Map<string, OperationWithSourceInfo<TemplateCreateOperation>[]>
  >
  layeredMetaModel: Map<Optional<string>, MetaModel>
  seen: Set<string>
  collector: TemplateTypeReferenceNodes[]
}): TemplateReferenceVersionNode => {
  const metaModel = getLayerMetaModelFromMap({
    layeredMetaModel,
    layerName: operation.layer.layerId,
  })

  return {
    _type: 'TemplateReferenceVersionNode',
    fromDate: operation!.internalOperation.entity!.templateFromDate ?? null,
    fields: getReferenceProperties(
      operation.internalOperation.entity as AbstractTemplateInOut,
      metaModel,
    )
      .map((f) =>
        collectTemplateFieldReferenceNode({
          variants: f,
          operations,
          layeredMetaModel,
          seen,
          collector,
        }),
      )
      .filter((f) => f.references && f.references.length > 0),
  }
}

const collectTemplateFieldReferenceNode = ({
  variants,
  operations,
  layeredMetaModel,
  seen,
  collector,
}: {
  variants: VariantsAndProperty
  operations: Map<
    string,
    Map<string, OperationWithSourceInfo<TemplateCreateOperation>[]>
  >
  layeredMetaModel: Map<Optional<string>, MetaModel>
  seen: Set<string>
  collector: TemplateTypeReferenceNodes[]
}): TemplateReferenceFieldNode => {
  const variantProperty = variants.property
  const templateTypeName = (
    variantProperty.onlyTemplatedType as Optional<ModelMetaType>
  )?.templateType?.typeName as Optional<string>

  collectTemplateTypeReferenceNodes({
    currentType: templateTypeName ?? '',
    operations,
    seen,
    collector,
    layeredMetaModel,
  })

  return {
    _type: 'TemplateReferenceFieldNode',
    fieldName: variantProperty.name,
    references: variants.variants,
    referenceTypeName: templateTypeName,
  }
}

const navigateTemplateToReferences = <T extends AbstractTemplateInOut>(
  template: T,
  property: PropertyDescriptor,
) => {
  const fieldValue = template.properties
    ?.filter(notEmpty)
    .find((templateProperty) => templateProperty.name === property.name)
    ?.value as Optional<SubTemplateValueSetupInOut>

  return (
    fieldValue?.variants?.map((variant) => variant?.name).filter(notEmpty) || []
  )
}

const idFromRawNode = (node: TemplateReferenceNode | null) =>
  (node as unknown as { _id: string })?._id

export const transformResult = ({
  data,
  meta,
  layer,
  block,
}: {
  data: TemplateTypeReferenceNodes
  meta: MetaModel
  layer: LayerKey
  block: string
}) => {
  const list = (
    data.nodes?.flatMap((typeNode) =>
      typeNode?.rootReferences?.map((node) => [
        idFromRawNode(node),
        node as TemplateReferenceNode,
      ]),
    ) ?? []
  ).filter((n) => n) as [string, TemplateReferenceNode][]

  const rawMap = new Map(list)
  const resultMap = new Map<string, TemplateReferenceNodeFull>()
  data.nodes?.forEach((typeNode) =>
    typeNode?.rootReferences?.filter(notEmpty).forEach((n) =>
      transformNode({
        node: n as TemplateReferenceNode,
        typeName: typeNode?.typeName as string,
        rawMap,
        resultMap,
        meta,
        layer,
        block,
      }),
    ),
  )

  return resultMap
}

const transformNode = ({
  node,
  typeName,
  rawMap,
  resultMap,
  meta,
  block,
  layer,
}: {
  node: TemplateReferenceNode
  block: string
  layer: LayerKey
  typeName: string
  rawMap: Map<string, TemplateReferenceNode>
  resultMap: Map<string, TemplateReferenceNodeFull>
  meta: MetaModel
}): TemplateReferenceNodeFull => {
  const resultNode = resultMap.get(idFromRawNode(node))

  if (resultNode) {
    return resultNode
  }

  const resolvedNode: TemplateReferenceNodeFull = {
    id: idFromRawNode(node) as string,
    typeName,
    type: meta.types.get(typeName) as Optional<ModelMetaType>,
    referencedBy: [],
    versions: [],
    block,
    layer,
  }

  resultMap.set(resolvedNode.id, resolvedNode)

  const rawVersions = node.versions

  if (!rawVersions) {
    return resolvedNode
  }

  const groupedVersions = [
    ...rawVersions
      .reduce((map, v) => {
        if (v) {
          map.set(v?.fromDate ?? null, v)
        }

        return map
      }, new Map<string | null, TemplateReferenceVersionNode>())
      .values(),
  ]

  const versionsAndIntervals = groupedVersions.map((v, i) => {
    const nextVersion = groupedVersions[i + 1]
    const toDate = nextVersion?.fromDate ?? null

    return {
      version: v,
      fromDate: v?.fromDate ?? null,
      toDate,
    }
  })

  resolvedNode.versions = versionsAndIntervals.map((v) =>
    transformVersion({
      rawVersion: v.version as TemplateReferenceVersionNode,
      owningNode: resolvedNode,
      fromDate: v.fromDate,
      toDate: v.toDate,
      rawMap,
      resultMap,
      meta,
      layer,
      block,
    }),
  ) as TemplateVersionReferenceNodeFull[]

  return resolvedNode
}

const transformVersion = ({
  rawVersion,
  owningNode,
  fromDate,
  toDate,
  rawMap,
  resultMap,
  meta,
  layer,
  block,
}: {
  layer: LayerKey
  block: string
  rawVersion: TemplateReferenceVersionNode
  owningNode: TemplateReferenceNodeFull
  fromDate: string | null
  toDate: string | null
  rawMap: Map<string, TemplateReferenceNode>
  resultMap: Map<string, TemplateReferenceNodeFull>
  meta: MetaModel
}): TemplateVersionReferenceNodeFull => {
  const version: TemplateVersionReferenceNodeFull = {
    owningNode,
    activeInterval: {
      from: stringToDateTimeNullSafe(fromDate, Number.NEGATIVE_INFINITY),
      to: stringToDateTimeNullSafe(toDate, Number.POSITIVE_INFINITY),
    },
    fields: [],
  }

  if (rawVersion.fields) {
    version.fields = rawVersion.fields.map((f) =>
      transformField({
        rawField: f as TemplateReferenceFieldNode,
        owningVersion: version,
        rawMap,
        resultMap,
        meta,
        layer,
        block,
      }),
    )
  }

  return version
}

const transformField = ({
  rawField,
  owningVersion,
  rawMap,
  resultMap,
  meta,
  layer,
  block,
}: {
  rawField: TemplateReferenceFieldNode
  owningVersion: TemplateVersionReferenceNodeFull
  rawMap: Map<string, TemplateReferenceNode>
  resultMap: Map<string, TemplateReferenceNodeFull>
  meta: MetaModel
  layer: LayerKey
  block: string
}): TemplateFieldReferenceFull => {
  const field: TemplateFieldReferenceFull = {
    fieldName: rawField.fieldName as string,
    referencedType: rawField.referenceTypeName
      ? (meta.types.get(
          rawField.referenceTypeName as string,
        ) as Optional<ModelMetaType>)
      : undefined,
    referencedTypeName: rawField.referenceTypeName,
    owningVersion,
    referencedNodes: [],
  }

  if (rawField.references) {
    field.referencedNodes = rawField.references
      .filter((r) => rawMap.get(r as string))
      .map((r) => {
        const node = transformNode({
          node: rawMap.get(r as string) as TemplateReferenceNode,
          typeName: rawField.referenceTypeName as string,
          rawMap,
          resultMap,
          meta,
          layer,
          block,
        })
        node.referencedBy = [...node.referencedBy, field]

        return node
      })
  }

  return field
}
